<template>
  <slotted-dialog :title="'Time Summary'" :externaltrigger="dialogtrigger" :externalCloseTrigger="closetrigger">
    <template v-slot:content>
      <v-container :key="'tsd-' + dialogtrigger" class="pb-0">
        <v-row class="mb-2" no-gutters>
          <v-col cols="auto">
            <v-checkbox v-model="splitByDate" :label="'Split by date'" class="pa-0 ma-0 mr-3" hide-details dense />
          </v-col>
          <v-col cols="auto">
            <v-checkbox v-model="splitByTechnician" :label="'Split by employment'" class="pa-0 ma-0" hide-details
              dense />
          </v-col>
          <v-col cols="auto">
            <v-checkbox v-model="showTotals" :label="'Show total hours'" class="pa-0 ma-0 ml-3" hide-details dense />
          </v-col>
        </v-row>

        <!-- TWO-LAYER: Date -> Technician (collapsible headers) -->
        <q-scroll v-if="twoLayerMode" :list="summaryItemsFlat" :unfilteredcount="summaryItemsFlat.length"
          :loading="false" :init-limit="150" height="50vh" :scrollcheck-trigger="scrollcheck">
          <template v-slot:list-item="{ item }">
            <!-- Date header -->
            <DateSubHeader v-if="item._type === 'dateHeader'" :date="item.date || '—'" :items="item.items || []"
              :selectionFilter="selectionFilter" :setSelectionFilterFunc="setSelectionFilter"
              @checkScroll="onCheckScroll" />

            <!-- Technician header -->
            <EmploymentSubHeader v-else-if="item._type === 'techHeader'" class="pl-3" :date="item.date || '—'"
              :employment-id="item.employmentId || '—'" :label="getEmploymentName(item.employmentId)"
              :selectionFilter="selectionFilter" :setSelectionFilterFunc="setSelectionFilter"
              @checkScroll="onCheckScroll" />

            <!-- Row (hidden if parent date/tech collapsed) -->
            <v-list-item v-else-if="item._type === 'row' && !isRowHidden(item)" class="pl-9">
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <v-icon v-text="typeIcon(item)" size="18" class="mr-2" />
                  <span><strong>{{ item.articleNumber }}</strong></span>
                  <v-spacer></v-spacer>
                  <!-- RIGHT SIDE: HH:mm + precise hours -->
                  <span class="d-flex align-center text-no-wrap" style="gap: 16px;">
                    <span class="font-weight-medium">{{ formatNumber(item.articleCount) }}</span>
                    <span>({{ formatHoursMinutes(item.articleCount) }})</span>
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-else-if="item._type === 'techTotal' && showTotals && !isRowHidden(item)" class="pl-9">
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <span class="font-weight-medium">{{ $t("servicesystem.common_objects.commonterms.total") }}</span>
                  <v-spacer></v-spacer>
                  <span class="d-flex align-center text-no-wrap" style="gap: 16px;">
                    <span class="font-weight-medium">
                      {{ formatNumber(item.articleCount) }}
                    </span>
                    <span>
                      ({{ formatHoursMinutes(item.articleCount) }})
                    </span>
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <div v-else></div>
          </template>
        </q-scroll>

        <!-- SINGLE-LAYER (date OR technician) and NO-GROUP -->
        <q-scroll v-else :list="summaryItemsSingle" :unfilteredcount="summaryItemsSingle.length" :loading="false"
          :group-by="groupByProp" :init-limit="100" height="50vh" :scrollcheck-trigger="scrollcheck">
          <!-- Group header -->
          <template v-slot:group-header="{ group }">
            <DateSubHeader v-if="splitByDate && !splitByTechnician" :date="String(group || '—')" :items="[]"
              :selectionFilter="selectionFilter" :setSelectionFilterFunc="setSelectionFilter"
              @checkScroll="onCheckScroll" />
            <EmploymentSubHeader v-else-if="!splitByDate && splitByTechnician" :employment-id="String(group || '—')"
              :label="getEmploymentName(String(group || '—'))" :selectionFilter="selectionFilter"
              :setSelectionFilterFunc="setSelectionFilter" @checkScroll="onCheckScroll" />
            <GenericListSubHeader v-else :text="String(group || '')" :sticky="true" />
          </template>

          <!-- Items: hide when their group's header is collapsed -->
          <template v-slot:list-item="{ item }">
            <!-- Normal article rows (grouped or ungrouped) -->
            <v-list-item v-if="item._type !== 'totalAll'" v-show="!isSingleItemHidden(item)" :class="groupByProp ? 'pl-6' : ''">
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <v-icon v-text="typeIcon(item)" size="18" class="mr-2" />
                  <span><strong>{{ item.articleNumber }}</strong></span>
                  <v-spacer></v-spacer>
                  <!-- RIGHT SIDE: HH:mm + precise hours -->
                  <span class="d-flex align-center text-no-wrap" style="gap: 16px;">
                    <span class="font-weight-medium">{{ formatNumber(item.articleCount) }}</span>
                    <span>({{ formatHoursMinutes(item.articleCount) }})</span>
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <!-- Total row when NOT grouped at all -->
            <v-list-item v-else-if="item._type === 'totalAll' && showTotals" :class="groupByProp ? 'pl-6' : ''">
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <span class="font-weight-medium">
                    {{ $t("servicesystem.common_objects.commonterms.total") }}
                  </span>
                  <v-spacer></v-spacer>
                  <span class="d-flex align-center text-no-wrap" style="gap: 16px;">
                    <span class="font-weight-medium">
                      {{ formatNumber(item.articleCount) }}
                    </span>
                    <span>
                      ({{ formatHoursMinutes(item.articleCount) }})
                    </span>
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <div v-else></div>
          </template>

          <template v-slot:group-footer="{ group, items }">
            <v-list-item v-show="!isGroupHidden(group) && showTotals" :class="groupByProp ? 'pl-6' : ''">
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <span class="font-weight-medium">
                    {{ $t("servicesystem.common_objects.commonterms.total") }}
                  </span>
                  <v-spacer></v-spacer>
                  <span class="d-flex align-center text-no-wrap" style="gap: 16px;">
                    <span class="font-weight-medium">
                      {{ formatNumber(groupTotal(items)) }}
                    </span>
                    <span>
                      ({{ formatHoursMinutes(groupTotal(items)) }})
                    </span>
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </q-scroll>
      </v-container>
    </template>

    <template v-slot:buttons>
      <v-btn @click="closetrigger++" color="primary" outlined
        v-text="$t('servicesystem.common_objects.commonterms.cancel')">
      </v-btn>
    </template>
  </slotted-dialog>
</template>

<script>
import SlottedDialog from "@/views/layouts/SlottedDialog.vue";
import QScroll from "@/components/Sections/Containers/QScroll.vue";
import GenericListSubHeader from "@/components/ListItems/GenericListSubHeader.vue";
import DateSubHeader from "@/components/ListItems/DateSubHeader.vue";
import EmploymentSubHeader from "@/components/ListItems/EmploymentSubHeader.vue";

/** LocalStorage keys */
const LS_KEYS = {
  splitByDate: "TimeSummaryDialog.splitByDate",
  splitByTechnician: "TimeSummaryDialog.splitByTechnician",
  showTotals: "TimeSummaryDialog.showTotals",
};

export default {
  name: "TimeSummaryDialog",
  props: {
    externaltrigger: Number,
    orderDetails: { type: Array, default: () => [] },
    employmentMap: { type: [Object, Map], default: null },
  },
  components: { SlottedDialog, QScroll, GenericListSubHeader, DateSubHeader, EmploymentSubHeader },

  data() {
    return {
      dialogtrigger: 0,
      closetrigger: 0,
      splitByTechnician: false,
      splitByDate: false,
      showTotals: false,
      selectionFilter: { hideBy: {} },
      scrollcheck: 0,
      articleTypeMap: {},
    };
  },

  created() {
    // Restore persisted toggles (safe parse + defaults)
    this.splitByDate = this._getBoolLS(LS_KEYS.splitByDate, this.splitByDate);
    this.splitByTechnician = this._getBoolLS(LS_KEYS.splitByTechnician, this.splitByTechnician);
    this.showTotals = this._getBoolLS(LS_KEYS.showTotals, this.showTotals);
  },

  methods: {
    // precise hours with suffix
    formatNumber(n) {
      const val = typeof n === "number" ? n : parseFloat(n) || 0;
      return new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }).format(val) + " x";
    },

    // hours + minutes (HH:mm), hours can exceed 24
    formatHoursMinutes(n) {
      const val = typeof n === "number" ? n : parseFloat(n) || 0;
      const totalMinutes = Math.round(val * 60);
      const sign = totalMinutes < 0 ? -1 : 1;
      const abs = Math.abs(totalMinutes);
      const hours = Math.floor(abs / 60) * sign;
      const minutes = abs % 60;
      return `${hours}:${String(minutes).padStart(2, "0")} h`;
    },

    groupTotal(items) {
      if (!Array.isArray(items) || !items.length) return 0;
      return items.reduce((sum, it) => {
        const v = typeof it.articleCount === "number"
          ? it.articleCount
          : parseFloat(it.articleCount) || 0;
        return sum + v;
      }, 0);
    },

    isGroupHidden(group) {
      const key = String(group || "—");

      // same logic as isSingleItemHidden, but for the group itself
      if (this.splitByDate && !this.splitByTechnician) {
        return this.isDateHidden(key);
      }

      if (!this.splitByDate && this.splitByTechnician) {
        return !!this.selectionFilter.hideBy?.[key];
      }

      // no grouping -> never hide (footer isn't used in that mode anyway)
      return false;
    },

    typeIcon(row) {
      const type = this.articleTypeMap[row.articleNumber] || "Work";
      return type === "Drive" ? "mdi-car" : "mdi-clock";
    },

    // employment.Name lookup
    getEmploymentName(id) {
      if (!id || id === "—" || id === "no employment") return "—";
      const m = this.employmentMap;
      let entry;
      if (m && typeof m.get === "function") entry = m.get(id);
      else if (m && typeof m === "object") entry = m[id];
      return (entry && (entry.Name || entry.name)) || id;
    },

    // collapse helpers
    isDateHidden(date) { return !!this.selectionFilter.hideBy?.[String(date)]; },
    isTechHidden(date, empId) {
      const key = `${String(date)}::${String(empId || "—")}`;
      return !!this.selectionFilter.hideBy?.[key];
    },
    isSingleItemHidden(item) {
      if (this.splitByDate && !this.splitByTechnician) return this.isDateHidden(item.groupKey);
      if (!this.splitByDate && this.splitByTechnician) return !!this.selectionFilter.hideBy?.[String(item.groupKey)];
      return false;
    },
    isRowHidden(item) {
      return this.isDateHidden(item.reportDate || "—") ||
        this.isTechHidden(item.reportDate || "—", item.employmentId || "—");
    },

    setSelectionFilter(copy) { this.selectionFilter = copy || { hideBy: {} }; },
    onCheckScroll() { this.scrollcheck++; },

    // ---- localStorage helpers ----
    _getBoolLS(key, defaultVal) {
      try {
        const raw = localStorage.getItem(key);
        if (raw === null) return defaultVal;
        const parsed = JSON.parse(raw);
        if (typeof parsed === "boolean") return parsed;
        // accept "1"/"0" or "true"/"false" strings
        if (raw === "1" || raw === "true") return true;
        if (raw === "0" || raw === "false") return false;
        return defaultVal;
      } catch {
        return defaultVal;
      }
    },
    _setBoolLS(key, val) {
      try {
        localStorage.setItem(key, JSON.stringify(!!val));
      }
      catch {
        // ignore
      }
    },
  },

  computed: {
    twoLayerMode() { return this.splitByDate && this.splitByTechnician; },

    groupByProp() {
      if (this.splitByDate && !this.splitByTechnician) return "groupKey";
      if (!this.splitByDate && this.splitByTechnician) return "groupKey";
      return null;
    },

    // Normalization
    _normalizedDetails() {
      const details = Array.isArray(this.orderDetails) ? this.orderDetails : [];
      const toDate = (v) => {
        if (!v) return "";
        const d = v instanceof Date ? v : new Date(v);
        if (isNaN(d.getTime())) return "";
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${d.getFullYear()}-${mm}-${dd}`;
      };
      const s = (v) => (v === null || v === undefined) ? "" : String(v);
      const n = (v) => (typeof v === "number" ? v : parseFloat(v) || 0);

      return details.map(d => ({
        articleNumber: s(d.ArticleNumber),
        articleDescription: s(d.ArticleDescription),
        employmentId: s(d.EmploymentId),
        reportDate: toDate(d.EmploymentTimeReportDate),
        articleCount: n(d.ArticleCount),
      }));
    },

    // Base for two-layer
    baseBuckets() {
      const buckets = new Map();
      for (const d of this._normalizedDetails) {
        const key = [d.articleNumber, d.reportDate, d.employmentId].join("|");
        if (!buckets.has(key)) {
          buckets.set(key, {
            articleNumber: d.articleNumber,
            articleDescription: d.articleDescription,
            reportDate: d.reportDate || "—",
            employmentId: d.employmentId || "—",
            articleCount: 0,
          });
        }
        buckets.get(key).articleCount += d.articleCount;
      }
      return Array.from(buckets.values());
    },

    // No-group: merge by article
    byArticle() {
      const map = new Map();
      for (const d of this._normalizedDetails) {
        const k = d.articleNumber;
        if (!map.has(k)) {
          map.set(k, { articleNumber: d.articleNumber, articleDescription: d.articleDescription, articleCount: 0 });
        }
        map.get(k).articleCount += d.articleCount;
      }
      return Array.from(map.values()).sort((a, b) =>
        (a.articleNumber || "").localeCompare(b.articleNumber || "")
      );
    },

    byDateArticle() {
      const map = new Map(); // date => Map(article => row)
      for (const d of this._normalizedDetails) {
        const date = d.reportDate || "—";
        if (!map.has(date)) map.set(date, new Map());
        const inner = map.get(date);
        const k = d.articleNumber;
        if (!inner.has(k)) {
          inner.set(k, { groupKey: date, reportDate: date, articleNumber: d.articleNumber, articleCount: 0 });
        }
        inner.get(k).articleCount += d.articleCount;
      }
      return map;
    },

    byTechArticle() {
      const map = new Map(); // empId => Map(article => row)
      for (const d of this._normalizedDetails) {
        const tech = d.employmentId || "—";
        if (!map.has(tech)) map.set(tech, new Map());
        const inner = map.get(tech);
        const k = d.articleNumber;
        if (!inner.has(k)) {
          inner.set(k, { groupKey: tech, employmentId: tech, articleNumber: d.articleNumber, articleCount: 0 });
        }
        inner.get(k).articleCount += d.articleCount;
      }
      return map;
    },

    // Two-layer list
    summaryItemsFlat() {
      if (!this.twoLayerMode) return [];
      const byDate = new Map();
      for (const r of this.baseBuckets) {
        const date = r.reportDate || "—";
        const tech = r.employmentId || "—";
        if (!byDate.has(date)) byDate.set(date, new Map());
        const techMap = byDate.get(date);
        if (!techMap.has(tech)) techMap.set(tech, []);
        techMap.get(tech).push({ _type: "row", ...r });
      }

      const result = [];
      const datesSorted = Array.from(byDate.keys()).sort((a, b) => a.localeCompare(b));
      for (const date of datesSorted) {
        result.push({ _type: "dateHeader", date });
        if (this.isDateHidden(date)) continue;

        const techMap = byDate.get(date);
        const techsSorted = Array.from(techMap.keys()).sort((a, b) => a.localeCompare(b));
        for (const tech of techsSorted) {
          result.push({ _type: "techHeader", date, employmentId: tech });
          if (this.isTechHidden(date, tech)) continue;

          const rows = techMap.get(tech).slice().sort((a, b) =>
            (a.articleNumber || "").localeCompare(b.articleNumber || "")
          );
          result.push(...rows);

          const total = rows.reduce((sum, r) => sum + (r.articleCount || 0), 0);
          result.push({
            _type: "techTotal",
            date,
            reportDate: date,       // so isRowHidden works
            employmentId: tech,     // so isRowHidden works
            articleNumber: "Total",
            articleCount: total,
          });
        }
      }
      return result;
    },

    // Single-layer/no-group
    summaryItemsSingle() {
      if (!this.splitByDate && !this.splitByTechnician) {
        const rows = this.byArticle.slice();
        if (rows.length) {
          const total = rows.reduce((sum, r) => sum + (r.articleCount || 0), 0);
          rows.push({
            _type: "totalAll",
            articleNumber: "Total", // label is overridden in template with $t()
            articleCount: total,
          });
        }
        return rows;
      }
      if (this.splitByDate && !this.splitByTechnician) {
        const out = [];
        const datesSorted = Array.from(this.byDateArticle.keys()).sort((a, b) => a.localeCompare(b));
        for (const date of datesSorted) {
          const inner = this.byDateArticle.get(date);
          const rows = Array.from(inner.values()).sort((a, b) =>
            (a.articleNumber || "").localeCompare(b.articleNumber || "")
          );
          out.push(...rows);
        }
        return out;
      }
      if (!this.splitByDate && this.splitByTechnician) {
        const out = [];
        const techsSorted = Array.from(this.byTechArticle.keys()).sort((a, b) => {
          const na = this.getEmploymentName(a);
          const nb = this.getEmploymentName(b);
          return String(na).localeCompare(String(nb));
        });
        for (const tech of techsSorted) {
          const inner = this.byTechArticle.get(tech);
          const rows = Array.from(inner.values()).sort((a, b) =>
            (a.articleNumber || "").localeCompare(b.articleNumber || "")
          );
          out.push(...rows);
        }
        return out;
      }
      return [];
    },
  },

  watch: {
    // Persist map & refresh dialog on open
    externaltrigger() {
      this.articleTypeMap = this.orderDetails.reduce((map, obj) => {
        map[obj.ArticleNumber] = obj.Type;
        return map;
      }, {});
      this.dialogtrigger++;
    },

    // Persist toggles to localStorage whenever they change
    splitByDate(val) {
      this._setBoolLS(LS_KEYS.splitByDate, val);
    },
    splitByTechnician(val) {
      this._setBoolLS(LS_KEYS.splitByTechnician, val);
    },
    showTotals(val) {
      this._setBoolLS(LS_KEYS.showTotals, val);
    },
  },
};
</script>